// src/components/BannedScreen.jsx
// شاشة إيقاف الحساب — تظهر للمستخدمين الموقوفين
// مع دعم نظام استرداد الديون: عرض المبلغ المستحق ورفع وصل الدفع
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { Env } from '../config/env';
import { FiShield, FiHeadphones, FiLogOut, FiAlertTriangle, FiUpload, FiCheck, FiX, FiCopy, FiCamera } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString('ar-DZ', { maximumFractionDigits: 0 });
};

const BannedScreen = ({ banReason: banReasonProp = '', debtAmount: debtAmountProp = 0, pendingPayments: pendingPaymentsProp = [] }) => {
  const navigate = useNavigate();
  const { user, userType, signOut } = useAuth();
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loadingDebt, setLoadingDebt] = useState(false);

  // استخدام القيم الممررة كقيم أولية، ثم تحديثها من الخادم
  const [banReason, setBanReason] = useState(banReasonProp);
  const [debtAmount, setDebtAmount] = useState(debtAmountProp);
  const [pendingPayments, setPendingPayments] = useState(pendingPaymentsProp);

  const hasDebt = debtAmount > 0;
  const ccpAccount = Env.ccpAccountNumber || '';

  // 🆕 جلب بيانات الإيقاف والديون عند التحميل (مطلوب لصفحة /banned المباشرة)
  useEffect(() => {
    const fetchBanInfo = async () => {
      if (!user?.id || user.id === 'guest' || user.isGuest) return;
      if (banReasonProp !== '' || debtAmountProp > 0) {
        // القيم مُمررة من BanGuard، لا حاجة لجلب مرة أخرى إذا كانت موجودة
        // لكن نحاول تحديثها لضمان حداثة البيانات
        return;
      }

      setLoadingDebt(true);
      try {
        let accountData = null;
        if (userType === 'provider') {
          accountData = await supabaseService.getProviderById(user.id);
        } else if (userType === 'client') {
          accountData = await supabaseService.getClientById(user.id);
        } else {
          const [providerResult, clientResult] = await Promise.allSettled([
            supabaseService.getProviderById(user.id),
            supabaseService.getClientById(user.id),
          ]);
          accountData = (providerResult.status === 'fulfilled' && providerResult.value)
            ? providerResult.value
            : ((clientResult.status === 'fulfilled' && clientResult.value) ? clientResult.value : null);
        }

        if (accountData) {
          const reason = accountData.banReason || accountData.ban_reason || '';
          if (!banReason) setBanReason(reason);

          const total = await supabaseService.getPendingCommissionsTotal(user.id);
          setDebtAmount(total);

          const payments = await supabaseService.getPendingPayments(user.id);
          setPendingPayments(payments);
        }
      } catch (error) {
        console.error('❌ Error fetching ban info:', error);
      } finally {
        setLoadingDebt(false);
      }
    };

    fetchBanInfo();
  }, [user?.id, userType, banReasonProp, debtAmountProp]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      window.location.href = '/auth';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // التحقق من نوع الملف والحجم
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('يرجى رفع صورة بصيغة JPG أو PNG');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
      return;
    }

    setReceiptFile(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const handleSubmitReceipt = async () => {
    if (!receiptFile) {
      toast.error('يرجى اختيار صورة وصل الدفع أولاً');
      return;
    }
    if (!user?.id || !userType) {
      toast.error('بيانات المستخدم غير متوفرة');
      return;
    }

    setUploading(true);
    try {
      // 1. رفع الصورة إلى التخزين
      const receiptUrl = await supabaseService.uploadReceipt(receiptFile);
      if (!receiptUrl) throw new Error('فشل رفع وصل الدفع');

      // 2. ربط الوصل بربط جميع الفواتورات المعلقة
      const payments = pendingPayments.length > 0
        ? pendingPayments
        : await supabaseService.getPendingPayments(user.id);

      if (payments.length === 0) {
        toast.error('لم يتم العثور على فاتورة معلقة لربطها بالوصل');
        return;
      }

      for (const payment of payments) {
        await supabaseService.submitPaymentReceipt(payment.id, receiptUrl);
      }

      setSubmitted(true);
      setReceiptFile(null);
      setReceiptPreview(null);
      toast.success('تم إرسال الوصل بنجاح! سيتم مراجعته خلال 24 ساعة', { duration: 5000 });
    } catch (error) {
      console.error('Error submitting receipt:', error);
      toast.error('فشل إرسال الوصل: ' + (error.message || 'خطأ غير معروف'));
    } finally {
      setUploading(false);
    }
  };

  const copyAccountNumber = () => {
    if (!ccpAccount) return;
    navigator.clipboard.writeText(ccpAccount);
    toast.success('تم نسخ رقم الحساب البريدي', { duration: 2000 });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-lg" style={{ maxWidth: '420px' }}>
        <div className="bg-white rounded-3xl shadow-2xl border border-red-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-l from-red-600 to-red-500 p-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
              <FiShield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-black text-white mb-1">تم إيقاف حسابك</h1>
            <p className="text-red-100 text-sm">عذراً، تم تعطيل حسابك من قبل الإدارة</p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Ban reason */}
            <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-2xl p-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <FiAlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-bold text-red-800 mb-1">سبب الإيقاف</p>
                <p className="text-sm text-red-700 leading-relaxed">
                  {banReason || 'لم يتم تحديد سبب محدد. يرجى التواصل مع الدعم الفني للمزيد من المعلومات.'}
                </p>
              </div>
            </div>

            {/* Debt Recovery Section — يظهر فقط إذا كان هناك دين */}
            {hasDebt && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-amber-800 flex items-center gap-2">
                      <FiAlertTriangle className="w-4 h-4" />
                      ديون غير مسددة
                    </span>
                  </div>

                  {/* Total amount */}
                  <div className="bg-white rounded-xl border border-amber-100 p-4 text-center mb-4">
                    <p className="text-xs text-amber-600 font-bold mb-1">إجمالي المبلغ المستحق</p>
                    <p className="text-3xl font-black text-red-600">
                      {formatCurrency(debtAmount)} <span className="text-sm font-semibold text-gray-500">د.ج</span>
                    </p>
                  </div>

                  {/* CCP Account */}
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
                    <p className="text-xs text-gray-500 font-semibold mb-2">رقم الحساب البريدي (CCP) للدفع</p>
                    <div className="flex items-center justify-between bg-white border-2 border-dashed border-gray-200 rounded-xl px-4 py-3">
                      <code className="text-sm font-bold text-gray-900 break-all text-center flex-1">
                        {ccpAccount || 'غير محدد'}
                      </code>
                      {ccpAccount && (
                        <button
                          onClick={copyAccountNumber}
                          className="mr-2 p-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 transition-colors flex items-center justify-center shrink-0"
                          title="نسخ رقم الحساب"
                        >
                          <FiCopy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Receipt upload — يظهر فقط بعد الدفع وإذا لم يتم الإرسال بالفعل */}
                  {!submitted ? (
                    <div className="space-y-3">
                      <p className="text-xs text-gray-600 mt-2">
                        بعد إتمام المدفوعات، ارفع صورة الوصل هنا لمراجعة الإدارة.
                      </p>

                      {/* Upload area */}
                      <div
                        onClick={() => document.getElementById('receipt-input').click()}
                        className="border-2 border-dashed border-gray-200 hover:border-gray-400 rounded-xl p-4 cursor-pointer text-center transition-colors"
                      >
                        {receiptPreview ? (
                          <div className="relative">
                            <img src={receiptPreview} alt="preview" className="w-full h-32 object-cover rounded-lg" />
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setReceiptFile(null);
                                setReceiptPreview(null);
                              }}
                              className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition"
                            >
                              <FiX size={11} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-2 text-gray-400 py-3">
                            <FiCamera size={20} />
                            <span className="text-xs font-medium">اضغط لرفع صورة الوصل</span>
                          </div>
                        )}
                        <input
                          id="receipt-input"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>

                      {/* Submit button */}
                      <button
                        onClick={handleSubmitReceipt}
                        disabled={uploading || !receiptFile}
                        className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-3 rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors disabled:opacity-60"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            جاري الرفع...
                          </>
                        ) : (
                          <>
                            <FiUpload size={16} />
                            إرسال الوصل
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <FiCheck className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="text-sm font-bold text-green-800">
                        تم إرسال الوصل بنجاح!
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        سيراجع المسؤول طلبك خلال 24 ساعة.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loading debt data */}
            {loadingDebt && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-gray-900 mx-auto mb-2" />
                <p className="text-xs text-gray-500">جاري تحميل بيانات الديون...</p>
              </div>
            )}

            {/* Info (only when no debt and no loading) */}
            {!hasDebt && !loadingDebt && (
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 text-center">
                <p className="text-sm text-gray-600 leading-relaxed">
                  إذا كنت تعتقد أن هذا الإيقاف غير صحيح، يرجى التواصل مع فريق الدعم الفني وسنقوم بمراجعة حالتك في أقرب وقت ممكن.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <a
                href="https://khidmadz.shop/contact-us"
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-3.5 rounded-xl font-bold text-sm hover:bg-gray-800 transition-colors"
              >
                <FiHeadphones className="w-4 h-4" />
                تواصل معنا
              </a>
              <button
                onClick={handleSignOut}
                className="w-full flex items-center justify-center gap-2 border border-gray-200 text-gray-600 py-3.5 rounded-xl font-bold text-sm hover:bg-gray-50 transition-colors"
              >
                <FiLogOut className="w-4 h-4" />
                تسجيل الخروج
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} خدمة — جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
};

export default BannedScreen;
